/**
 * MCP Tools for Customer Briefings
 * 
 * Intelligent aggregation tool for meeting preparation
 */

import { MCPTool } from '../types';
import { leadsService } from '../../services/leadsService';
import { tasksService } from '../../services/tasksService';
import { opportunitiesService } from '../../services/opportunitiesService';
import { companiesService } from '../../services/companiesService';
import { getMCPServer } from '../server/MCPServer';

// ============================================================================
// GET CUSTOMER BRIEFING
// ============================================================================

export const getCustomerBriefingTool: MCPTool = {
  name: 'get_customer_briefing',
  description: 'Erstellt ein umfassendes Briefing für ein Kundentermin. Use when user says: "Gib mir ein Briefing zu...", "Bereite Meeting vor", "Termin vorbereiten mit...", "Bereite Termin vor", "Was muss ich wissen über...", "Kundeninfo zu...", "Meeting prep", "Vorbereitung Kunde". Sammelt automatisch alle relevanten CRM-Daten aus der Datenbank: Lead-Info, Opportunities, Tasks, Company-Daten, letzte Aktivitäten. WICHTIG: Funktioniert sowohl mit Personennamen (personName) als auch Firmennamen (companyName).',
  
  inputSchema: {
    type: 'object',
    properties: {
      personName: {
        type: 'string',
        description: 'Name der Person (Lead) für das Briefing. Beispiel: "Max Mustermann" oder "Sarah Johnson". Entweder personName ODER companyName muss angegeben werden.'
      },
      companyName: {
        type: 'string',
        description: 'Firmenname alternativ. Beispiel: "Microsoft" oder "TechStartup Inc". Entweder personName ODER companyName muss angegeben werden.'
      },
      includeHistory: {
        type: 'boolean',
        description: 'Ob Interaktionshistorie einbezogen werden soll (default: true)'
      }
    }
  },

  async execute(params, context) {
    const briefing: any = {
      generatedAt: new Date().toISOString(),
      query: params.personName || params.companyName,
      sections: {}
    };

    try {
      // 1. Find the lead
      let targetLead = null;
      let relatedLeads = [];
      
      if (params.personName) {
        const allLeads = await leadsService.getLeads(context.userId, context.orgId);
        targetLead = allLeads.find(lead => 
          lead.name?.toLowerCase().includes(params.personName.toLowerCase()) ||
          `${lead.firstName} ${lead.lastName}`.toLowerCase().includes(params.personName.toLowerCase())
        );
        
        if (!targetLead) {
          return {
            success: false,
            error: `Keine Lead gefunden für: ${params.personName}. Bitte prüfe den Namen oder erstelle zuerst einen Lead.`
          };
        }

        briefing.sections.lead = {
          name: targetLead.name || `${targetLead.firstName} ${targetLead.lastName}`,
          email: targetLead.email,
          phone: targetLead.phone,
          company: targetLead.company,
          position: targetLead.position || targetLead.title,
          status: targetLead.status || targetLead.stage,
          score: targetLead.score,
          source: targetLead.source,
          tags: targetLead.tags || [],
          notes: targetLead.notes,
          createdAt: targetLead.createdAt
        };
      }

      // 2. Find related company
      if (targetLead?.company || params.companyName) {
        const companySearchName = params.companyName || targetLead?.company || '';
        const allCompanies = await companiesService.getCompanies(context.orgId);
        
        const relatedCompany = allCompanies.find(c => 
          c.name?.toLowerCase().includes(companySearchName.toLowerCase())
        );

        if (relatedCompany) {
          briefing.sections.company = {
            name: relatedCompany.name,
            industry: relatedCompany.industry,
            website: relatedCompany.website,
            phone: relatedCompany.phone,
            email: relatedCompany.email,
            employeeCount: relatedCompany.employeeCount,
            revenue: relatedCompany.revenue,
            isCustomer: relatedCompany.isCustomer,
            description: relatedCompany.description,
            tags: relatedCompany.tags || []
          };

          // Find all leads from this company
          if (params.companyName) {
            const allLeads = await leadsService.getLeads(context.userId, context.orgId);
            relatedLeads = allLeads.filter(lead => 
              lead.company?.toLowerCase().includes(companySearchName.toLowerCase())
            );
            
            briefing.sections.allContactsFromCompany = relatedLeads.map(l => ({
              name: l.name || `${l.firstName} ${l.lastName}`,
              position: l.position || l.title,
              email: l.email,
              status: l.status || l.stage
            }));
          }
        }
      }

      // 3. Find related opportunities
      const allOpportunities = await opportunitiesService.getOpportunities(context.userId, context.orgId);
      const relatedOpportunities = allOpportunities.filter(opp => {
        if (targetLead) {
          return opp.leadIds?.includes(targetLead.id) || 
                 opp.companyName?.toLowerCase().includes(targetLead.company?.toLowerCase() || '');
        }
        if (params.companyName) {
          return opp.companyName?.toLowerCase().includes(params.companyName.toLowerCase());
        }
        return false;
      });

      if (relatedOpportunities.length > 0) {
        briefing.sections.opportunities = relatedOpportunities.map(opp => ({
          title: opp.title,
          value: opp.value,
          stage: opp.stage,
          probability: opp.probability,
          expectedCloseDate: opp.expectedCloseDate,
          description: opp.description
        }));

        const totalValue = relatedOpportunities.reduce((sum, o) => sum + (o.value || 0), 0);
        briefing.sections.opportunitySummary = {
          count: relatedOpportunities.length,
          totalValue: totalValue,
          stages: relatedOpportunities.map(o => o.stage)
        };
      }

      // 4. Find related tasks
      const allTasks = await tasksService.getTasks(context.userId, context.orgId);
      const relatedTasks = allTasks.filter(task => {
        if (targetLead) {
          return task.leadId === targetLead.id || task.contactId === targetLead.id ||
                 task.title?.toLowerCase().includes(targetLead.name?.toLowerCase() || '') ||
                 task.description?.toLowerCase().includes(targetLead.name?.toLowerCase() || '');
        }
        if (params.companyName) {
          return task.title?.toLowerCase().includes(params.companyName.toLowerCase()) ||
                 task.description?.toLowerCase().includes(params.companyName.toLowerCase());
        }
        return false;
      });

      if (relatedTasks.length > 0) {
        const openTasks = relatedTasks.filter(t => t.status !== 'completed');
        const overdueTasks = openTasks.filter(t => 
          new Date(t.dueDate) < new Date()
        );

        briefing.sections.tasks = {
          total: relatedTasks.length,
          open: openTasks.length,
          overdue: overdueTasks.length,
          upcoming: openTasks.slice(0, 5).map(t => ({
            title: t.title,
            dueDate: t.dueDate,
            priority: t.priority,
            status: t.status
          }))
        };
      }

      // 5. Create briefing summary
      briefing.summary = this.generateBriefingSummary(briefing.sections, params);
      briefing.success = true;

      return briefing;

    } catch (error: any) {
      console.error('Error generating briefing:', error);
      return {
        success: false,
        error: `Fehler beim Erstellen des Briefings: ${error.message}`
      };
    }
  },

  // Helper function to generate summary text
  generateBriefingSummary(sections: any, params: any): string {
    const parts = [];
    
    // Lead info
    if (sections.lead) {
      const l = sections.lead;
      parts.push(`KONTAKT: ${l.name}`);
      if (l.position) parts.push(`Position: ${l.position}`);
      if (l.company) parts.push(`Firma: ${l.company}`);
      if (l.email) parts.push(`Email: ${l.email}`);
      if (l.phone) parts.push(`Tel: ${l.phone}`);
      if (l.status) parts.push(`Status: ${l.status}`);
      if (l.score) parts.push(`Lead Score: ${l.score}/100`);
    }

    // Company info
    if (sections.company) {
      const c = sections.company;
      parts.push(`\nFIRMA: ${c.name}`);
      if (c.industry) parts.push(`Branche: ${c.industry}`);
      if (c.employeeCount) parts.push(`Mitarbeiter: ${c.employeeCount}`);
      if (c.revenue) parts.push(`Umsatz: ${c.revenue}`);
      if (c.isCustomer) parts.push(`Status: Bestehender Kunde ✅`);
    }

    // Multiple contacts from company
    if (sections.allContactsFromCompany && sections.allContactsFromCompany.length > 1) {
      parts.push(`\nWEITERE KONTAKTE (${sections.allContactsFromCompany.length}):`);
      sections.allContactsFromCompany.slice(0, 5).forEach((contact: any) => {
        parts.push(`  • ${contact.name} (${contact.position}) - ${contact.status}`);
      });
    }

    // Opportunities
    if (sections.opportunities && sections.opportunities.length > 0) {
      parts.push(`\nOPPORTUNITIES (${sections.opportunities.length}):`);
      sections.opportunities.forEach((opp: any) => {
        parts.push(`  • ${opp.title}: €${opp.value.toLocaleString()} (${opp.stage}, ${opp.probability}% Wahrscheinlichkeit)`);
      });
      if (sections.opportunitySummary) {
        parts.push(`  → Gesamt: €${sections.opportunitySummary.totalValue.toLocaleString()}`);
      }
    }

    // Tasks
    if (sections.tasks) {
      const t = sections.tasks;
      parts.push(`\nAUFGABEN:`);
      parts.push(`  Offen: ${t.open}, Überfällig: ${t.overdue}`);
      if (t.upcoming && t.upcoming.length > 0) {
        parts.push(`  Nächste Aufgaben:`);
        t.upcoming.forEach((task: any) => {
          parts.push(`    • ${task.title} (${task.priority}, fällig: ${new Date(task.dueDate).toLocaleDateString()})`);
        });
      }
    }

    return parts.join('\n');
  }
};

// ============================================================================
// REGISTER BRIEFING TOOLS
// ============================================================================

export function registerBriefingTools(): void {
  const server = getMCPServer();
  
  server.registerTool(getCustomerBriefingTool, {
    category: 'briefing',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  console.log('✅ Registered 1 briefing tool');
}

